import "../../App.css";
import "./Dashboard.css";

import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArrowCircleDown,
    FaArrowCircleUp,
    FaCheckCircle,
    FaClock,
    FaCoins,
    FaPiggyBank,
    FaTimesCircle,
    FaTrophy,
    FaUserCircle,
    FaUsers,
    FaWallet,
} from "react-icons/fa";
import {
    NotificationContainer,
    NotificationManager,
} from "react-notifications";
import { DatePicker, Input, Modal } from "antd";
import dayjs from "dayjs";
import axios from "axios";

import Appconfig from "../../config/config";
import {
    WebSocketContext,
} from "../../context/websocket";

const INITIAL_DASHBOARD_DATA = {
    userBalance: 0,
    downlineBalance: 0,
    totalBalance: 0,
    winnings: 0,

    deposits: {
        approved: 0,
        approvedAmount: 0,

        pending: 0,
        pendingAmount: 0,

        cancelled: 0,
        cancelledAmount: 0,

        total: 0,
        totalAmount: 0,
    },

    withdrawals: {
        approved: 0,
        approvedAmount: 0,

        pending: 0,
        pendingAmount: 0,

        cancelled: 0,
        cancelledAmount: 0,

        total: 0,
        totalAmount: 0,
    },
};

const normalizeRequestStatistics = (group) => {
    const data = group || {};

    return {
        approved: Number(
            data.approved?.count ??
            data.approved ??
            0
        ),

        approvedAmount: Number(
            data.approved?.amount ??
            data.approvedAmount ??
            0
        ),

        pending: Number(
            data.pending?.count ??
            data.pending ??
            0
        ),

        pendingAmount: Number(
            data.pending?.amount ??
            data.pendingAmount ??
            0
        ),

        cancelled: Number(
            data.cancelled?.count ??
            data.rejected?.count ??
            data.cancelled ??
            data.rejected ??
            0
        ),

        cancelledAmount: Number(
            data.cancelled?.amount ??
            data.rejected?.amount ??
            data.cancelledAmount ??
            data.rejectedAmount ??
            0
        ),

        total: Number(
            data.total?.count ??
            data.total ??
            0
        ),

        totalAmount: Number(
            data.total?.amount ??
            data.totalAmount ??
            0
        ),
    };
};

function Dashboard() {
    const navigate = useNavigate();
    const websocket =
        useContext(WebSocketContext);

    const liveRefreshTimer =
        useRef(null);

    const userInfo = JSON.parse(
        localStorage.getItem("userdata") || "null"
    );

    const [dashboardData, setDashboardData] = useState(
        INITIAL_DASHBOARD_DATA
    );

    /*
 * Calendar initially shows a one-week range.
 * Changing these values does not call the API.
 */
    const [dateFilter, setDateFilter] =
        useState({
            from: dayjs()
                .subtract(7, "day")
                .startOf("day"),

            to: dayjs().endOf("day"),
        });

    /*
     * Dashboard counts initially use today's range.
     * This state changes only after Today,
     * Yesterday, or Apply Filter is clicked.
     */
    const [
        appliedDateFilter,
        setAppliedDateFilter,
    ] = useState({
        from: dayjs().startOf("day"),
        to: dayjs().endOf("day"),
    });

    const [activeDatePreset, setActiveDatePreset] =
        useState("today");

    const [
        transactionDataLoading,
        setTransactionDataLoading,
    ] = useState(false);

    const [
        transactionFilterError,
        setTransactionFilterError,
    ] = useState("");

    const [dashboardLoading, setDashboardLoading] =
        useState(true);

    const [dashboardError, setDashboardError] =
        useState("");

    const [userNameValidationError, setUserNameValidationError] =
        useState("Please enter old password");

    const [passwordValidationError, setPasswordValidationError] =
        useState("Please enter new password");

    const [
        confirmPasswordValidationError,
        setConfirmPasswordValidationError,
    ] = useState("Please check your confirm password");

    const [formData, setFormData] = useState({
        user_id: userInfo?._id || "",
        name: userInfo?.name || "",
        user_name: userInfo?.user_name || "",
        user_type: userInfo?.user_type || "",
        old_password: "",
        new_password: "",
        con_password: "",
    });

    const formatCurrency = (value) => {
        const numericValue = Number(value) || 0;

        return new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(numericValue);
    };

    const formatCount = (value) => {
        return new Intl.NumberFormat("en-IN").format(
            Number(value) || 0
        );
    };

    const formatApiDateTime = (date) => {
        return date.format("YYYY-MM-DD HH:mm:ss");
    };

    const setPasswordField = (fieldName, value) => {
        setFormData((previous) => ({
            ...previous,
            [fieldName]: value,
        }));
    };

    const checkValidation = () => {
        let isValid = true;

        if (!formData.old_password.trim()) {
            setUserNameValidationError(
                "Please enter old password"
            );
            isValid = false;
        }

        if (!formData.new_password.trim()) {
            setPasswordValidationError(
                "Please enter new password"
            );
            isValid = false;
        }

        if (
            formData.con_password.trim() !==
            formData.new_password.trim()
        ) {
            setConfirmPasswordValidationError(
                "New password and confirm password must match"
            );
            isValid = false;
        }

        return isValid;
    };

    const changePassword = async () => {
        if (!checkValidation()) {
            return;
        }

        try {
            const response = await axios.post(
                `${Appconfig.apiUrl}users/auraChangePassword`,
                {
                    ...formData,
                    master_id: userInfo?._id,
                    masters: userInfo?.masters,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.result) {
                NotificationManager.success(
                    response.data.message,
                    "",
                    3000
                );

                setFormData((previous) => ({
                    ...previous,
                    old_password: "",
                    new_password: "",
                    con_password: "",
                }));

                setTimeout(() => {
                    localStorage.removeItem("userdata");
                    localStorage.removeItem("login_token");
                    navigate("/login");
                }, 3000);

                return;
            }

            NotificationManager.error(
                response.data.message,
                "",
                3000
            );
        } catch (error) {
            console.error("Password change failed:", error);

            NotificationManager.error(
                "Unable to change password",
                "",
                3000
            );
        }
    };

    const getUserBalance = useCallback(async () => {
        const response = await axios.post(
            `${Appconfig.apiUrl}ledger/getUserBalance`,
            {
                user_id: userInfo?._id,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return Number(
            response.data?.resultData?.balance || 0
        );
    }, [userInfo?._id]);

    const getDashboardStatistics = useCallback(
        async (fromDate, toDate) => {
            const response = await axios.post(
                `${Appconfig.apiUrl}dashboard/getDashboardStatistics`,
                {
                    user_id: userInfo?._id,
                    site_code: Appconfig.sitecodes,

                    from_date: formatApiDateTime(fromDate),
                    to_date: formatApiDateTime(toDate),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            return response.data?.resultData || {};
        },
        [userInfo?._id]
    );

    const updateTransactionCounts = useCallback(
        (statistics) => {
            if (!statistics) {
                return;
            }

            const depositStatistics =
                statistics?.deposits ||
                statistics?.deposit;

            const withdrawalStatistics =
                statistics?.withdrawals ||
                statistics?.withdraw;

            /*
             * Invalid background response should not
             * overwrite valid values with zero.
             */
            if (
                !depositStatistics &&
                !withdrawalStatistics
            ) {
                return;
            }

            setDashboardData((previous) => ({
                ...previous,

                deposits: depositStatistics
                    ? normalizeRequestStatistics(
                        depositStatistics
                    )
                    : previous.deposits,

                withdrawals: withdrawalStatistics
                    ? normalizeRequestStatistics(
                        withdrawalStatistics
                    )
                    : previous.withdrawals,
            }));
        },
        []
    );

    const loadDashboardData = useCallback(async () => {
        if (!userInfo?._id) {
            setDashboardError(
                "User information not found."
            );
            setDashboardLoading(false);
            return;
        }

        setDashboardLoading(true);
        setDashboardError("");

        try {
            const [userBalance, statistics] =
                await Promise.all([
                    getUserBalance(),

                    /*
                     * Initial dashboard data will always
                     * show today's statistics.
                     */
                    getDashboardStatistics(
                        dayjs().startOf("day"),
                        dayjs().endOf("day")
                    ),
                ]);

            const depositStatistics =
                statistics?.deposits ||
                statistics?.deposit;

            const withdrawalStatistics =
                statistics?.withdrawals ||
                statistics?.withdraw;

            setDashboardData((previous) => ({
                ...previous,

                userBalance: Number(
                    statistics?.userBalance ??
                    userBalance ??
                    previous.userBalance ??
                    0
                ),

                downlineBalance: Number(
                    statistics?.downlineBalance ??
                    previous.downlineBalance ??
                    0
                ),

                totalBalance: Number(
                    statistics?.totalBalance ??
                    previous.totalBalance ??
                    0
                ),

                winnings: Number(
                    statistics?.winnings ??
                    previous.winnings ??
                    0
                ),

                deposits: depositStatistics
                    ? normalizeRequestStatistics(
                        depositStatistics
                    )
                    : previous.deposits,

                withdrawals: withdrawalStatistics
                    ? normalizeRequestStatistics(
                        withdrawalStatistics
                    )
                    : previous.withdrawals,
            }));
        } catch (error) {
            console.error(
                "Dashboard data loading failed:",
                error
            );

            setDashboardError(
                error?.response?.data?.resultMessage ||
                "Unable to load dashboard data."
            );
        } finally {
            setDashboardLoading(false);
        }
    }, [
        userInfo?._id,
        getUserBalance,
        getDashboardStatistics,
    ]);

    const applyTransactionFilter = useCallback(
        async (fromDate, toDate) => {
            if (!fromDate || !toDate) {
                setTransactionFilterError(
                    "Please select both From and To date-time."
                );
                return;
            }

            if (fromDate.isAfter(toDate)) {
                setTransactionFilterError(
                    "The From date-time cannot be later than the To date-time."
                );
                return;
            }

            setTransactionDataLoading(true);
            setTransactionFilterError("");

            try {
                const statistics =
                    await getDashboardStatistics(
                        fromDate,
                        toDate
                    );

                updateTransactionCounts(statistics);
            } catch (error) {
                console.error(
                    "Transaction filter failed:",
                    error
                );

                setTransactionFilterError(
                    "Unable to load deposit and withdrawal statistics."
                );
            } finally {
                setTransactionDataLoading(false);
            }
        },
        [
            getDashboardStatistics,
            updateTransactionCounts,
        ]
    );

    const handleTodayFilter = () => {
        const todayRange = {
            from: dayjs().startOf("day"),
            to: dayjs().endOf("day"),
        };

        setAppliedDateFilter(todayRange);
        setActiveDatePreset("today");

        applyTransactionFilter(
            todayRange.from,
            todayRange.to
        );
    };

    const handleYesterdayFilter = () => {
        const yesterday =
            dayjs().subtract(1, "day");

        const yesterdayRange = {
            from:
                yesterday.startOf("day"),
            to: yesterday.endOf("day"),
        };

        setAppliedDateFilter(
            yesterdayRange
        );

        setActiveDatePreset(
            "yesterday"
        );

        applyTransactionFilter(
            yesterdayRange.from,
            yesterdayRange.to
        );
    };

    const handleCustomDateFilter = () => {
        if (
            !dateFilter.from ||
            !dateFilter.to
        ) {
            setTransactionFilterError(
                "Please select both From and To date-time."
            );

            return;
        }

        if (
            dateFilter.from.isAfter(
                dateFilter.to
            )
        ) {
            setTransactionFilterError(
                "The From date-time cannot be later than the To date-time."
            );

            return;
        }

        const customRange = {
            from: dateFilter.from,
            to: dateFilter.to,
        };

        setAppliedDateFilter(customRange);
        setActiveDatePreset("custom");

        applyTransactionFilter(
            customRange.from,
            customRange.to
        );
    };

    const refreshLiveDashboard =
        useCallback(async () => {
            if (!userInfo?._id) {
                return;
            }

            try {
                const [userBalance, statistics] =
                    await Promise.all([
                        getUserBalance(),

                        getDashboardStatistics(
                            appliedDateFilter.from,
                            appliedDateFilter.to
                        ),
                    ]);

                /*
                 * Balance safely update hoga. Missing values
                 * current values ko zero nahi karengi.
                 */
                setDashboardData((previous) => ({
                    ...previous,

                    userBalance: Number(
                        userBalance ??
                        previous.userBalance ??
                        0
                    ),

                    downlineBalance: Number(
                        statistics?.downlineBalance ??
                        previous.downlineBalance ??
                        0
                    ),

                    totalBalance: Number(
                        statistics?.totalBalance ??
                        previous.totalBalance ??
                        0
                    ),

                    winnings: Number(
                        statistics?.winnings ??
                        previous.winnings ??
                        0
                    ),
                }));

                /*
                 * Deposit and withdrawal values always use
                 * the same flat-state normalizer.
                 */
                updateTransactionCounts(statistics);
            } catch (error) {
                console.error(
                    "Live dashboard refresh failed:",
                    error
                );

                /*
                 * Do not reset current dashboard data when
                 * a background request fails.
                 */
            }
        }, [
            userInfo?._id,
            appliedDateFilter.from,
            appliedDateFilter.to,
            getUserBalance,
            getDashboardStatistics,
            updateTransactionCounts,
        ]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    useEffect(() => {
        if (
            !websocket ||
            typeof websocket.addEventListener !==
            "function"
        ) {
            return undefined;
        }

        const handleDashboardSocketMessage =
            (event) => {
                try {
                    const message =
                        typeof event.data ===
                            "string"
                            ? JSON.parse(
                                event.data
                            )
                            : event.data;

                    const liveActions = [
                        "USER_BLOCK_UPDATE",
                        "DEPOSIT_REQUEST_UPDATE",
                        "WITHDRAW_REQUEST_UPDATE",
                        "PAYMENT_REQUEST_UPDATE",
                        "DASHBOARD_UPDATE",
                    ];

                    if (
                        !liveActions.includes(
                            message?.action
                        )
                    ) {
                        return;
                    }

                    /*
                     * Prevent multiple rapid API calls if
                     * several WebSocket messages arrive together.
                     */
                    if (
                        liveRefreshTimer.current
                    ) {
                        clearTimeout(
                            liveRefreshTimer.current
                        );
                    }

                    liveRefreshTimer.current =
                        setTimeout(() => {
                            refreshLiveDashboard();
                        }, 300);
                } catch (error) {
                    console.error(
                        "Unable to process dashboard WebSocket message:",
                        error
                    );
                }
            };

        websocket.addEventListener(
            "message",
            handleDashboardSocketMessage
        );

        return () => {
            websocket.removeEventListener(
                "message",
                handleDashboardSocketMessage
            );

            if (
                liveRefreshTimer.current
            ) {
                clearTimeout(
                    liveRefreshTimer.current
                );
            }
        };
    }, [
        websocket,
        refreshLiveDashboard,
    ]);

    useEffect(() => {
        const pollingInterval =
            setInterval(() => {
                if (
                    document.visibilityState ===
                    "visible"
                ) {
                    refreshLiveDashboard();
                }
            }, 15000);

        return () =>
            clearInterval(
                pollingInterval
            );
    }, [refreshLiveDashboard]);

    const balanceCards = [
        {
            title: "My Balance",
            value: dashboardData.userBalance,
            icon: FaWallet,
            className: "dashboard-balance-blue",
        },
        {
            title: "Downline Balance",
            value: dashboardData.downlineBalance,
            icon: FaUsers,
            className: "dashboard-balance-purple",
        },
        {
            title: "Total Balance",
            value: dashboardData.totalBalance,
            icon: FaCoins,
            className: "dashboard-balance-cyan",
        },
        {
            title: "Total Winnings",
            value: dashboardData.winnings,
            icon: FaTrophy,
            className: "dashboard-balance-gold",
        },
    ];

    const depositCards = [
        {
            title: "Approved",
            count: Number(
                dashboardData?.deposits?.approved || 0
            ),
            amount: Number(
                dashboardData?.deposits?.approvedAmount || 0
            ),
            icon: FaCheckCircle,

            // CSS class ke liye
            status: "approved",

            // URL filtering ke liye
            filter: "approved",
        },
        {
            title: "Pending",
            count: Number(
                dashboardData?.deposits?.pending || 0
            ),
            amount: Number(
                dashboardData?.deposits?.pendingAmount || 0
            ),
            icon: FaClock,
            status: "pending",
            filter: "pending",
        },
        {
            title: "Rejected",
            count: Number(
                dashboardData?.deposits?.cancelled || 0
            ),
            amount: Number(
                dashboardData?.deposits?.cancelledAmount || 0
            ),
            icon: FaTimesCircle,
            status: "cancelled",
            filter: "rejected",
        },
        {
            title: "All Deposits",
            count: Number(
                dashboardData?.deposits?.total || 0
            ),
            amount: Number(
                dashboardData?.deposits?.totalAmount || 0
            ),
            icon: FaPiggyBank,
            status: "total",
            filter: "all",
        },
    ];

    const withdrawalCards = [
        {
            title: "Approved",
            count: Number(
                dashboardData?.withdrawals?.approved || 0
            ),
            amount: Number(
                dashboardData?.withdrawals?.approvedAmount || 0
            ),
            icon: FaCheckCircle,
            status: "approved",
            filter: "approved",
        },
        {
            title: "Pending",
            count: Number(
                dashboardData?.withdrawals?.pending || 0
            ),
            amount: Number(
                dashboardData?.withdrawals?.pendingAmount || 0
            ),
            icon: FaClock,
            status: "pending",
            filter: "pending",
        },
        {
            title: "Rejected",
            count: Number(
                dashboardData?.withdrawals?.cancelled || 0
            ),
            amount: Number(
                dashboardData?.withdrawals?.cancelledAmount || 0
            ),
            icon: FaTimesCircle,
            status: "cancelled",
            filter: "rejected",
        },
        {
            title: "All Withdrawals",
            count: Number(
                dashboardData?.withdrawals?.total || 0
            ),
            amount: Number(
                dashboardData?.withdrawals?.totalAmount || 0
            ),
            icon: FaWallet,
            status: "total",
            filter: "all",
        },
    ];

    const handleRequestCardClick = (
        requestType,
        filter
    ) => {
        const targetPath =
            requestType === "deposit"
                ? "/deposit-request"
                : "/withdraw-request";

        // All card original page open karega.
        // Koi query filter apply nahi hoga.
        if (filter === "all") {
            navigate(targetPath);
            return;
        }

        navigate(
            `${targetPath}?status=${encodeURIComponent(
                filter
            )}`
        );
    };

    const renderRequestCard = (item, type) => {
        const Icon = item.icon;

        const displayCount = Number(
            item.count || 0
        );

        const displayAmount = Number(
            item.amount || 0
        );

        const isLoading =
            dashboardLoading ||
            transactionDataLoading;

        return (
            <button
                type="button"
                key={`${type}-${item.status}`}
                className={`dashboard-request-card dashboard-request-${item.status}`}
                onClick={() =>
                    handleRequestCardClick(
                        type,
                        item.filter
                    )
                }
                disabled={isLoading}
                aria-label={`Open ${item.title}`}
            >
                <div className="dashboard-request-icon">
                    <Icon aria-hidden="true" />
                </div>

                <div className="dashboard-request-content">
                    <span className="dashboard-request-title">
                        {item.title}
                    </span>

                    <strong className="dashboard-request-main-amount">
                        {isLoading
                            ? "—"
                            : `₹${displayAmount.toLocaleString(
                                "en-IN",
                                {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                }
                            )}`}
                    </strong>
                </div>

                <span
                    className="dashboard-request-count-badge"
                    title={`${displayCount} requests`}
                    aria-label={`${displayCount} ${item.title} requests`}
                >
                    {isLoading
                        ? "—"
                        : displayCount.toLocaleString(
                            "en-IN"
                        )}
                </span>
            </button>
        );
    };

    return (
        <>
            <NotificationContainer />

            {userInfo &&
                userInfo.is_password_update !== "Yes" && (
                    <Modal
                        className="password-model dashboard-password-modal"
                        footer={
                            <div className="dashboard-password-footer">
                                <button
                                    type="button"
                                    className="dashboard-password-submit"
                                    onClick={changePassword}
                                >
                                    Confirm
                                </button>
                            </div>
                        }
                        mask={false}
                        open
                        closable={false}
                    >
                        <div className="dashboard-password-heading">
                            Change Password
                        </div>

                        <div className="dashboard-password-body">
                            <div className="dashboard-password-field">
                                <label htmlFor="oldPassword">
                                    Old Password
                                </label>

                                <Input.Password
                                    id="oldPassword"
                                    name="oldPassword"
                                    value={formData.old_password}
                                    onChange={(event) =>
                                        setPasswordField(
                                            "old_password",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Old Password..."
                                />

                                {!formData.old_password && (
                                    <small>
                                        {userNameValidationError}
                                    </small>
                                )}
                            </div>

                            <div className="dashboard-password-field">
                                <label htmlFor="newPassword">
                                    New Password
                                </label>

                                <Input.Password
                                    id="newPassword"
                                    name="newPassword"
                                    value={formData.new_password}
                                    onChange={(event) =>
                                        setPasswordField(
                                            "new_password",
                                            event.target.value
                                        )
                                    }
                                    placeholder="New Password..."
                                />

                                {!formData.new_password && (
                                    <small>
                                        {passwordValidationError}
                                    </small>
                                )}
                            </div>

                            <div className="dashboard-password-field">
                                <label htmlFor="confirmPass">
                                    Confirm Password
                                </label>

                                <Input.Password
                                    id="confirmPass"
                                    name="confirmPass"
                                    value={formData.con_password}
                                    onChange={(event) =>
                                        setPasswordField(
                                            "con_password",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Confirm Password..."
                                />

                                {formData.con_password &&
                                    formData.new_password !==
                                    formData.con_password && (
                                        <small>
                                            {confirmPasswordValidationError}
                                        </small>
                                    )}
                            </div>
                        </div>
                    </Modal>
                )}

            <main className="premium-dashboard">
                <header className="dashboard-welcome">
                    <div>
                        <span className="dashboard-eyebrow">
                            Dashboard overview
                        </span>

                        <h1>
                            Welcome back,{" "}
                            {userInfo?.user_name ||
                                userInfo?.name ||
                                "User"}
                        </h1>

                        <p>
                            Balance and payment-request summary
                        </p>
                    </div>

                    <div className="dashboard-user-avatar">
                        <FaUserCircle aria-hidden="true" />
                    </div>
                </header>

                {dashboardError && (
                    <div
                        className="dashboard-error"
                        role="alert"
                    >
                        <span>{dashboardError}</span>

                        <button
                            type="button"
                            onClick={loadDashboardData}
                        >
                            Retry
                        </button>
                    </div>
                )}

                <section
                    className="dashboard-balance-grid"
                    aria-label="Balance summary"
                >
                    {balanceCards.map((card) => {
                        const Icon = card.icon;

                        return (
                            <article
                                className={`dashboard-balance-card ${card.className}`}
                                key={card.title}
                            >
                                <div className="dashboard-balance-details">
                                    <span>{card.title}</span>

                                    <strong>
                                        <small>₹</small>
                                        {dashboardLoading
                                            ? "—"
                                            : formatCurrency(card.value)}
                                    </strong>
                                </div>

                                <div className="dashboard-balance-icon">
                                    <Icon aria-hidden="true" />
                                </div>

                                <div className="dashboard-card-glow" />
                            </article>
                        );
                    })}
                </section>

                <section
                    className="dashboard-date-filter"
                    aria-label="Transaction date and time filter"
                >
                    <div className="dashboard-date-filter-heading">
                        <div className="dashboard-filter-heading-icon">
                            <FaClock aria-hidden="true" />
                        </div>

                        <div>
                            <h2>Transaction Filter</h2>
                            {/* <p>
                                Deposit and withdrawal requests ko date
                                aur exact time se filter karein
                            </p> */}
                        </div>
                    </div>

                    <div className="dashboard-date-filter-controls">
                        <div className="dashboard-date-field">
                            <label htmlFor="dashboard-from-date">
                                From
                            </label>

                            <DatePicker
                                id="dashboard-from-date"
                                className="dashboard-date-picker"
                                value={dateFilter.from}
                                format="DD MMM YYYY, hh:mm:ss A"
                                showTime={{
                                    format: "hh:mm:ss A",
                                    use12Hours: true,
                                }}
                                allowClear={false}
                                placeholder="Select from date and time"
                                onChange={(value) => {
                                    if (!value) {
                                        return;
                                    }

                                    setDateFilter((previous) => ({
                                        ...previous,
                                        from: value,
                                    }));

                                    setActiveDatePreset("custom");
                                }}
                            />
                        </div>

                        <div className="dashboard-date-field">
                            <label htmlFor="dashboard-to-date">
                                To
                            </label>

                            <DatePicker
                                id="dashboard-to-date"
                                className="dashboard-date-picker"
                                value={dateFilter.to}
                                format="DD MMM YYYY, hh:mm:ss A"
                                showTime={{
                                    format: "hh:mm:ss A",
                                    use12Hours: true,
                                }}
                                allowClear={false}
                                placeholder="Select to date and time"
                                onChange={(value) => {
                                    if (!value) {
                                        return;
                                    }

                                    setDateFilter((previous) => ({
                                        ...previous,
                                        to: value,
                                    }));

                                    setActiveDatePreset("custom");
                                }}
                            />
                        </div>

                        <button
                            type="button"
                            className={[
                                "dashboard-date-preset",
                                activeDatePreset === "today"
                                    ? "dashboard-date-preset-active"
                                    : "",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={handleTodayFilter}
                            disabled={transactionDataLoading}
                        >
                            Today
                        </button>

                        <button
                            type="button"
                            className={[
                                "dashboard-date-preset",
                                activeDatePreset === "yesterday"
                                    ? "dashboard-date-preset-active"
                                    : "",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={handleYesterdayFilter}
                            disabled={transactionDataLoading}
                        >
                            Yesterday
                        </button>

                        <button
                            type="button"
                            className="dashboard-date-apply"
                            onClick={handleCustomDateFilter}
                            disabled={transactionDataLoading}
                        >
                            {transactionDataLoading
                                ? "Loading..."
                                : "Apply Filter"}
                        </button>
                    </div>

                    {transactionFilterError && (
                        <div
                            className="dashboard-date-filter-error"
                            role="alert"
                        >
                            {transactionFilterError}
                        </div>
                    )}
                </section>

                <div className="dashboard-transactions-row">

                    <section className="dashboard-payment-section dashboard-deposit-section">
                        <div className="dashboard-section-header">
                            <div className="dashboard-section-title">
                                <span className="dashboard-section-icon">
                                    <FaArrowCircleDown aria-hidden="true" />
                                </span>

                                <div>
                                    <h2>Deposit Requests</h2>
                                    <p>
                                        Complete deposit request overview
                                    </p>
                                </div>
                            </div>

                            <span className="dashboard-section-badge">
                                Deposits
                            </span>
                        </div>

                        <div className="dashboard-request-grid">
                            {depositCards.map((item) =>
                                renderRequestCard(item, "deposit")
                            )}
                        </div>
                    </section>

                    <section className="dashboard-payment-section dashboard-withdrawal-section">
                        <div className="dashboard-section-header">
                            <div className="dashboard-section-title">
                                <span className="dashboard-section-icon">
                                    <FaArrowCircleUp aria-hidden="true" />
                                </span>

                                <div>
                                    <h2>Withdrawal Requests</h2>
                                    <p>
                                        Complete withdrawal request overview
                                    </p>
                                </div>
                            </div>

                            <span className="dashboard-section-badge">
                                Withdrawals
                            </span>
                        </div>

                        <div className="dashboard-request-grid">
                            {withdrawalCards.map((item) =>
                                renderRequestCard(
                                    item,
                                    "withdrawal"
                                )
                            )}
                        </div>
                    </section>

                </div>
            </main>
        </>
    );
}

export default Dashboard;